// @ts-strict-ignore
import type {
  Action,
  Attribute,
  DataRoute,
  ServiceObject,
  Threshold,
} from 'flow/store/engine/types';
import { actionHasRules } from 'flow/service/actions';
import { getDataRoutesForAction } from 'flow/service/dataRoutes';
import { getAllThresholdFromRules } from 'flow/service/thresholds';
import { getEquivalencyKeyForAttribute } from '../snippets/collisions/equivalency/equivalency';

export function indexServiceAttributesById(attributes: Attribute[]): Record<number, Attribute> {
  return Object.fromEntries(
    attributes.map(attribute => [attribute.attribute_id, attribute]),
  );
}

export function indexAttributesByToken(attributes: Attribute[]): Record<Attribute['attribute_token'], Attribute> {
  return Object.fromEntries(
    attributes.map(attribute => [attribute.attribute_token, attribute]),
  );
}

/**
 * Creates an index of all services' internal names by any of their attributes' token.
 * @example
 * {
 *  token_1: "service-a",
 *  token_2: "service-a",
 *  token_3: "service-b",
 *  token_4: "service-b",
 *  token_5: "service-c",
 *  token_6: "service-c",
 * }
 */
export function indexServiceInternalNamesByAttributeToken(
  services: ServiceObject[],
): Record<Attribute['attribute_token'], ServiceObject['internal_name']> {
  return Object.fromEntries(
    services.flatMap(service => {
      const serviceAttributes = getAttributesForServices(service);
      return serviceAttributes.map(attribute => [attribute.attribute_token, service.internal_name]);
    }),
  );
}


/**
 * Creates an index of all service attributes by their unique key.
 * @example
 * {
 *  "service-a::selector-1::display-name": attribute-1,
 *  "service-a::selector-2::display-name": attribute-2,
 *  "service-b::selector-1::display-name": attribute-3,
 *  "service-b::selector-2::display-name": attribute-4,
 * }
 */
export function indexAttributesByEquivalencyKey(
  services: ServiceObject[],
): Record<string, Attribute> {
  return Object.fromEntries(
    services.flatMap(service => {
      const attributesForService = getAttributesForServices(service);
      const attributeEntriesForService = attributesForService.map(attribute => {
        const serviceAttributeKey = getEquivalencyKeyForAttribute(
          attribute,
          service.internal_name,
        );
        return [serviceAttributeKey, attribute];
      });

      return attributeEntriesForService;
    }),
  );
}


/**
 * Returns the highest attribute_id across all services. Used when assigning unique IDs
 * to ephemeral attributes so new IDs do not collide with existing account attributes.
 */
export function getHighestAttributeId(services: ServiceObject[]): number {
  const ids = services.flatMap(service => Object.keys(service?.attributes ?? {}).map(Number));
  return Math.max(1, ...ids);
}


/**
 * Creates an assigner for ephemeral attribute IDs
 * Keys by equivalency key so the same attribute gets a stable ID across resolution across envs
 */
export function createEphemeralAttributeIdAssigner(highestAccountAttributeId: number): {
  getEphemeralAttributeId: (equivalencyKey: string) => number | undefined;
  assignEphemeralAttributeId: (equivalencyKey: string) => number;
} {
  const ephemeralAttrIds = new Map<string, number>();
  let nextEphemeralId = highestAccountAttributeId + 1;
  return {
    getEphemeralAttributeId(equivalencyKey: string): number | undefined {
      return ephemeralAttrIds.get(equivalencyKey);
    },
    assignEphemeralAttributeId(equivalencyKey: string): number {
      const id = nextEphemeralId;
      nextEphemeralId += 1;
      ephemeralAttrIds.set(equivalencyKey, id);
      return id;
    },
  };
}


/**
 * Collects attribute tokens from thresholds in action rules and data routes
 */
export function getUsedAttributeTokens(args: {
  actions: Record<string, Action>;
  thresholds: Record<string, Threshold>;
  dataRoutes: Record<string, DataRoute>;
}): Set<string> {
  const { actions, thresholds, dataRoutes } = args;
  const usedAttributeTokens = new Set<string>();
  for (const action of Object.values(actions)) {
    if (actionHasRules(action)) {
      const actionThresholds = getAllThresholdFromRules({
        rules: action.rules,
        thresholds,
      });
      for (const threshold of actionThresholds) {
        if (threshold.left?.attributeToken) usedAttributeTokens.add(threshold.left.attributeToken);
        if (threshold.right?.attributeToken && threshold.right.actionId) {
          usedAttributeTokens.add(threshold.right.attributeToken);
        }
      }
    }
    for (const dataRoute of getDataRoutesForAction({ action, dataRoutes })) {
      if (dataRoute.attribute?.token) usedAttributeTokens.add(dataRoute.attribute.token);
    }
  }
  return usedAttributeTokens;
}


/**
 * Returns attributes from the given service(s). When `includeOnly` is provided,
 * only attributes whose `attribute_token` is in the set or are null are returned
 * (e.g. for snippet insertion, where we only need attributes referenced in thresholds or data routes).
 */
export function getAttributesForServices(
  services: ServiceObject | ServiceObject[],
  options?: { includeOnly?: Set<string> },
): Attribute[] {
  const array = Array.isArray(services) ? services : [services];
  const attrs = array.flatMap(service => Object.values(service?.attributes ?? {}));
  if (options?.includeOnly != null) {
    const tokens = options.includeOnly;
    return attrs.filter(attr => tokens.has(attr?.attribute_token) || !attr?.attribute_token);
  }
  return attrs;
}
